import { Suspense, lazy } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { routes } from './app/routes.js';
import { NotFound } from './error/NotFound.jsx';
import { Layout } from './app/layout/Layout.jsx';
import { CommandPalette } from './shared/components/CommandPalette.jsx';
import { PageSpinner, SessionGate } from './shared/auth/SessionGate.jsx';
import GlobalContext from './shared/contexts/GlobalContext.jsx';
import { ShareView } from './pages/ShareView.jsx';
import { GuestShareView } from './pages/GuestShareView.jsx';
import { VerifyView } from './pages/VerifyView.jsx';

// docs' intent provider pages (docs.sign, docs.edit, docs.view, docs.pick),
// opened by another module's screen in an overlay or a new window. They sign
// the person in themselves (pages/intent/IntentPage.jsx), so they sit outside
// the gate. `/embed/sign` is docs.sign's page (E-IMZO signs on docs' host).
const IntentPage = lazy(() => import('./pages/intent/IntentPage.jsx'));

// Signed in: the command palette (search everywhere) over every screen.
function SignedInScreens() {
  return (
    <>
      <CommandPalette />
      <Outlet />
    </>
  );
}

function App() {
  return (
    <>
      <Toaster richColors position="top-right" closeButton />
      <GlobalContext>
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            {/* Nobody signs in to these: a guest link, a folder guest link,
                what the QR on a signed document opens (a guest signer proves
                a certificate there instead). */}
            <Route path="/share/:token" element={<ShareView />} />
            <Route path="/guest-share/:token" element={<GuestShareView />} />
            <Route path="/v/:code" element={<VerifyView />} />

            <Route path="/embed/sign" element={<IntentPage intent="docs.sign" />} />
            <Route path="/embed/intent/:intent" element={<IntentPage />} />

            <Route
              element={(
                <SessionGate>
                  <SignedInScreens />
                </SessionGate>
              )}
            >
              <Route path="/" element={<Navigate to="/my-docs" replace />} />
              {routes.filter((r) => r.bare).map((route) => {
                const { path, component: Page } = route;
                return <Route key={path} path={path} element={<Page />} />;
              })}
              <Route element={<Layout />}>
                {routes.filter((r) => !r.bare).map((route) => {
                  const { path, component: Page } = route;
                  return <Route key={path} path={path} element={<Page />} />;
                })}
                {/* Unknown paths keep the header: the way out is one click away. */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </GlobalContext>
    </>
  );
}

export default App;
